import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Lock, ShieldCheck, Check, Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

const Brand = () => (
  <img src="/brand/kewagpage-logo-white.png" alt="KEWAGPage" data-testid="force-pw-logo" draggable="false"
    className="w-[160px] h-auto object-contain block select-none" />
);

const inp = "mt-1.5 w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#A3E635] transition-colors";
const lbl = "text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400";

export default function ForcePasswordChange() {
  const { user, checkAuth, logout } = useAuth();
  const { t } = useI18n();
  const [step, setStep] = useState("password"); // password | offer | setup
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null); // {qr, secret}
  const [code, setCode] = useState("");

  const [error, setError] = useState("");

  const rules = [
    { ok: pw.length >= 10, text: t("Mindestens 10 Zeichen") },
    { ok: /[A-Z]/.test(pw) && /[a-z]/.test(pw), text: t("Gross- und Kleinbuchstaben") },
    { ok: /\d/.test(pw), text: t("Mindestens eine Ziffer") },
    { ok: /[^A-Za-z0-9]/.test(pw), text: t("Mindestens ein Sonderzeichen (z. B. ! ? % - _ .)") },
    { ok: pw.length > 0 && pw === pw2, text: t("Beide Passwörter stimmen überein") },
    { ok: pw.length > 0 && pw !== current, text: t("Unterscheidet sich vom Start-Passwort") },
  ];
  const allOk = rules.every((r) => r.ok);

  const finish = async () => {
    await checkAuth(); // globaler User wird aktualisiert -> Gate verschwindet, App erscheint
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (!allOk) return setError(t("Bitte alle Passwort-Regeln unten erfüllen."));
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: pw });
      if (data.token) localStorage.setItem("kewag_token", data.token); // Token-Rotation übernehmen
      toast.success(t("Passwort festgelegt."));
      if (user?.totp_enabled) await finish();
      else setStep("offer");
    } catch (err) {
      setError(err.response?.data?.detail || t("Passwort konnte nicht gesetzt werden."));
    } finally {
      setBusy(false);
    }
  };

  const start2fa = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable-start");
      setTwofa({ qr: data.qr, secret: data.secret });
      setCode("");
      setStep("setup");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("2FA konnte nicht gestartet werden."));
    } finally {
      setBusy(false);
    }
  };

  const confirm2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable-confirm", { code });
      toast.success(t("Zwei-Faktor-Authentifizierung aktiviert. Willkommen!"));
      await finish();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Code ungültig. Bitte erneut versuchen."));
    } finally {
      setBusy(false);
    }
  };

  const skip2fa = async () => {
    setBusy(true);
    try { await finish(); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0A0A0C]" data-testid="force-pw-screen">
      <div className="w-full max-w-md fade-in">
        <div className="flex justify-center mb-8"><Brand /></div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-sm p-6 sm:p-8 shadow-2xl shadow-black/40">

          {step === "password" && (
            <>
              <div className="w-11 h-11 rounded-xl bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center mb-4">
                <KeyRound className="w-5 h-5 text-[#A3E635]" />
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/25 px-2.5 py-1 rounded-full">{t("Erster Login")}</span>
              <h1 className="text-2xl font-black tracking-tighter mt-4">{t("Eigenes Passwort festlegen")}</h1>
              <p className="text-gray-400 mt-2 mb-6 text-sm leading-relaxed">
                {t("Aus Sicherheitsgründen müssen Sie beim ersten Login ein eigenes Passwort festlegen.")}
                {user?.email ? <span className="block mt-1 text-gray-500">{user.email}</span> : null}
              </p>
              <form onSubmit={submitPassword} className="space-y-4">
                <div>
                  <label className={lbl}>{t("Aktuelles / Start-Passwort")}</label>
                  <input data-testid="force-pw-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={inp} placeholder="••••••••" autoComplete="current-password" />
                </div>
                <div>
                  <label className={lbl}>{t("Neues Passwort")}</label>
                  <input data-testid="force-pw-new" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className={inp} placeholder="••••••••" autoComplete="new-password" />
                </div>
                <div>
                  <label className={lbl}>{t("Neues Passwort bestätigen")}</label>
                  <input data-testid="force-pw-confirm" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className={inp} placeholder="••••••••" autoComplete="new-password" />
                </div>
                <ul className="space-y-1.5 pt-1">
                  {rules.map((r, i) => (
                    <li key={i} className={`flex items-center gap-2 text-xs ${r.ok ? "text-[#A3E635]" : "text-gray-500"}`}>
                      <Check className={`w-3.5 h-3.5 ${r.ok ? "opacity-100" : "opacity-30"}`} /> {r.text}
                    </li>
                  ))}
                </ul>
                {error && (
                  <div data-testid="force-pw-error" className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>
                )}
                <button data-testid="force-pw-submit" type="submit" disabled={busy || !allOk}
                  className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                  {busy ? "..." : t("Weiter")}
                </button>
              </form>
            </>
          )}

          {step === "offer" && (
            <div data-testid="force-2fa-offer" className="fade-in">
              <div className="w-11 h-11 rounded-xl bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center mb-4">
                <ShieldCheck className="w-5 h-5 text-[#A3E635]" />
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/25 px-2.5 py-1 rounded-full">{t("Empfohlen")}</span>
              <h1 className="text-2xl font-black tracking-tighter mt-4">{t("Zusätzlich absichern")}</h1>
              <p className="text-gray-400 mt-2 mb-6 text-sm leading-relaxed">
                {t("Aktivieren Sie die Zwei-Faktor-Authentifizierung mit einer Authenticator-App — ein zweiter Code beim Login schützt Ihr Konto zusätzlich. Sie können das auch später unter «Konto & Sicherheit» tun.")}
              </p>
              <div className="space-y-3">
                <button data-testid="force-2fa-activate" onClick={start2fa} disabled={busy}
                  className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  <Smartphone className="w-4 h-4" /> {busy ? "..." : t("Zwei-Faktor jetzt aktivieren")}
                </button>
                <button data-testid="force-2fa-skip" onClick={skip2fa} disabled={busy}
                  className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                  {t("Später · direkt zum Cockpit")}
                </button>
              </div>
            </div>
          )}

          {step === "setup" && twofa && (
            <div data-testid="force-2fa-setup" className="fade-in">
              <div className="w-11 h-11 rounded-xl bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center mb-4">
                <Smartphone className="w-5 h-5 text-[#A3E635]" />
              </div>
              <h1 className="text-2xl font-black tracking-tighter">{t("2FA einrichten")}</h1>
              <p className="text-gray-400 mt-1 mb-5 text-sm">
                {t("Scannen Sie den QR-Code mit einer Authenticator-App (z. B. Google oder Microsoft Authenticator) und geben Sie den 6-stelligen Code ein.")}
              </p>
              <div className="mb-5">
                <div className="bg-white p-3 rounded-xl inline-block">
                  <img data-testid="force-2fa-qr" src={twofa.qr} alt={t("2FA QR-Code")} className="w-44 h-44" />
                </div>
                <div className="mt-3 text-xs text-gray-400">
                  {t("Kein Scan möglich? Schlüssel manuell eingeben:")}
                  <div data-testid="force-2fa-secret" className="mt-1 font-mono text-[11px] text-gray-200 break-all bg-white/5 border border-white/10 rounded px-2 py-1.5">{twofa.secret}</div>
                </div>
              </div>
              <form onSubmit={confirm2fa} className="space-y-4">
                <div>
                  <label className={lbl}>{t("6-stelliger Code")}</label>
                  <input data-testid="force-2fa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-gray-700 focus:outline-none focus:border-[#A3E635] transition-colors"
                    placeholder="000000" autoFocus />
                </div>
                <button data-testid="force-2fa-confirm" type="submit" disabled={busy || code.length !== 6}
                  className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                  {busy ? "..." : t("Aktivieren & fertig")}
                </button>
              </form>
              <button data-testid="force-2fa-back" onClick={() => setStep("offer")} className="mt-4 block text-xs text-gray-400 hover:text-white transition-colors">
                {t("← Zurück")}
              </button>
            </div>
          )}

          <button data-testid="force-pw-logout" onClick={logout} className="mt-5 block mx-auto text-xs text-gray-400 hover:text-white transition-colors">
            {t("Abmelden")}
          </button>
        </div>

        <div className="mt-6 flex items-center justify-center gap-4 text-[11px] text-gray-500">
          <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#A3E635]" /> {t("Verschlüsselte Verbindung")}</span>
          <span className="text-gray-700">·</span>
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#A3E635]" /> {t("Geschützter Kundenbereich")}</span>
        </div>
      </div>
    </div>
  );
}
